import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Printer, Star } from "lucide-react";
import JsBarcode from "jsbarcode";

export default function LoyaltyCard() {
  const { user } = useAuth();
  const [loyalty, setLoyalty] = useState(null);
  const [error, setError] = useState("");
  const [email, setEmail] = useState("");
  const barcodeRef = useRef(null);

  const load = (em) => {
    api
      .get("/loyalty/status", { params: { email: em } })
      .then((r) => {
        setLoyalty(r.data);
        setError("");
      })
      .catch((e) => setError(errMsg(e)));
  };

  useEffect(() => {
    if (user?.email) load(user.email);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (loyalty?.member_no && barcodeRef.current) {
      try {
        JsBarcode(barcodeRef.current, loyalty.member_no, {
          format: "CODE128",
          displayValue: false,
          height: 44,
          width: 1.8,
          background: "transparent",
          lineColor: "#f5f0e6",
          margin: 0,
        });
      } catch (e) {}
    }
  }, [loyalty]);

  if (!loyalty)
    return (
      <div data-testid="loyalty-card-access" className="max-w-md mx-auto px-6 pt-40 pb-24">
        <p className="text-[11px] font-ui tracking-[0.3em] uppercase text-primary">Easy Drive Loyalty</p>
        <h1 className="mt-3 font-display text-4xl tracking-tight">Your loyalty <span className="italic gold-text">card</span></h1>
        <p className="mt-4 text-sm text-muted-foreground">Enter the email you rent with to load your card.</p>
        <div className="mt-6 flex gap-3">
          <Input data-testid="loyalty-card-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" className="input-dark" />
          <Button data-testid="loyalty-card-load-button" onClick={() => load(email)} className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 font-ui shrink-0">
            Load
          </Button>
        </div>
        {error && <p data-testid="loyalty-card-error" className="mt-3 text-sm text-destructive font-ui">{error}</p>}
      </div>
    );

  return (
    <div data-testid="loyalty-card-page" className="max-w-lg mx-auto px-6 pt-32 pb-24">
      <div className="no-print mb-8 flex items-center justify-between flex-wrap gap-4">
        <Link to={user ? "/account" : "/"} className="text-sm font-ui text-muted-foreground hover:text-primary transition-colors duration-200">
          ← Back
        </Link>
        <Button data-testid="loyalty-card-print-button" variant="outline" onClick={() => window.print()} className="rounded-full border-border bg-transparent hover:bg-black/5 hover:text-primary font-ui">
          <Printer size={15} className="mr-2" /> Print / Save PDF
        </Button>
      </div>
      <p className="no-print mb-6 text-xs text-muted-foreground font-ui">On your phone, choose “Save as PDF” in the print dialog to keep your card in your files or wallet app.</p>

      <div className="print-area">
        <div
          data-testid="loyalty-card-visual"
          style={{ WebkitPrintColorAdjust: "exact", printColorAdjust: "exact", aspectRatio: "1.586" }}
          className="relative overflow-hidden rounded-3xl bg-[#140A0B] text-[#F5F0E6] p-7 shadow-[0_24px_60px_rgba(0,0,0,0.35)]"
        >
          <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-[#C71E2E]/25 blur-3xl" />
          <div className="absolute -bottom-24 -left-16 w-56 h-56 rounded-full bg-[#C71E2E]/15 blur-3xl" />
          <div className="relative flex items-start justify-between">
            <img src="/logo.png" alt="Easy Drive Car Rental" className="h-11 w-auto object-contain rounded-md bg-white/95 px-1.5 py-0.5" />
            <p className="text-[10px] font-ui tracking-[0.35em] uppercase text-[#F5F0E6]/70 mt-1">Loyalty</p>
          </div>
          <p data-testid="loyalty-card-tier" className="relative mt-6 font-display text-3xl tracking-tight">{loyalty.tier}</p>
          <p className="relative mt-1 text-xs font-ui text-[#E02434] font-semibold">
            {loyalty.percent ? `−${loyalty.percent}% on every rental` : "Earn discounts with every rental"}
          </p>
          <div className="relative mt-5">
            <p className="text-[9px] font-ui tracking-[0.3em] uppercase text-[#F5F0E6]/60">Member No.</p>
            <p data-testid="loyalty-card-member-no" className="mt-1 font-ui text-xl font-semibold tracking-[0.25em]">{loyalty.member_no}</p>
          </div>
          <div className="relative mt-4 flex items-end justify-between gap-4">
            <div className="min-w-0">
              {loyalty.perks && loyalty.perks.length > 0 ? (
                <ul data-testid="loyalty-card-perks" className="space-y-1">
                  {loyalty.perks.map((p) => (
                    <li key={p} className="flex items-center gap-1.5 text-[10px] font-ui text-[#F5F0E6]/85">
                      <Star size={9} className="text-[#E02434] shrink-0" /> {p}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-[10px] font-ui text-[#F5F0E6]/60">{loyalty.rentals} {loyalty.rentals === 1 ? "rental" : "rentals"} completed — keep roaming to unlock perks.</p>
              )}
            </div>
            <svg ref={barcodeRef} data-testid="loyalty-card-barcode" className="shrink-0 w-32 h-11" />
          </div>
          <p className="relative mt-4 text-[8px] font-ui tracking-[0.2em] uppercase text-[#F5F0E6]/50">
            Easy Drive Car Rental · Nassau, Bahamas · +1 (242) 813-1775
          </p>
        </div>
      </div>
    </div>
  );
}
